import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Clock,
  Bot,
  ArrowLeft,
  Mail,
  CalendarDays,
  Sparkles,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: "pending_review", label: "Request received", icon: Clock },
  { key: "vetting", label: "Vetting", icon: Bot },
  { key: "decision", label: "Decision", icon: Sparkles },
  { key: "booked", label: "Booked", icon: CheckCircle2 },
];

const FINAL_LOOKUP = {
  approved: { color: "#22c55e", icon: CheckCircle2, label: "Approved", copy: "You're confirmed. We're locking the date now — watch your inbox for a calendar invite and prep notes." },
  declined: { color: "#ff0055", icon: XCircle, label: "Declined", copy: "After review we aren't able to take this one on. Reply to your confirmation email and we can talk alternatives." },
  needs_info: { color: "#ffb800", icon: AlertTriangle, label: "Needs Info", copy: "We have one quick question before we can lock this in. Check the confirmation email — we'll be in touch shortly." },
  booked: { color: "#a78bfa", icon: CheckCircle2, label: "Locked in", copy: "All set — your session is officially on the calendar. We'll send a reminder the day before with parking and prep notes." },
};

const stepIndexFor = (status) => {
  if (status === "pending_review") return 0;
  if (status === "vetting") return 1;
  if (["approved", "declined", "needs_info"].includes(status)) return 2;
  if (status === "booked") return 3;
  return 0;
};

const BookingStatusPage = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchOne = async () => {
      try {
        const { data: res } = await axios.get(`${API}/bookings/track/${id}`, {
          timeout: 12000,
        });
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) setNotFound(err?.response?.status === 404);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchOne();
    // Poll every 30s for status updates
    const interval = setInterval(fetchOne, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05050a] text-white flex items-center justify-center" data-testid="booking-status-loading">
        <Loader2 className="animate-spin text-[#00f0ff]" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen bg-[#05050a] text-white" data-testid="booking-status-notfound">
        <Header />
        <main className="max-w-2xl mx-auto px-5 md:px-10 py-32 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-white/[0.04] border border-white/10 flex items-center justify-center mb-6">
            <XCircle size={28} className="text-white/50" />
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter">
            Booking not found
          </h1>
          <p className="text-[#a395c3] mt-4 max-w-md mx-auto">
            The link you followed may be wrong or the booking has been deleted.
            Double-check the URL or get in touch and we&apos;ll find it for you.
          </p>
          <a
            href="mailto:Info@sbmediastudio.com"
            className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#00f0ff] text-[#05050a] font-semibold text-sm hover:bg-white transition-colors"
            data-testid="booking-status-email-us"
          >
            <Mail size={14} /> Email Info@sbmediastudio.com
          </a>
        </main>
      </div>
    );
  }

  const activeIdx = stepIndexFor(data.status);
  const isFinal = ["approved", "declined", "needs_info", "booked"].includes(data.status);
  const final = FINAL_LOOKUP[data.status];

  return (
    <div className="min-h-screen bg-[#05050a] text-white" data-testid="booking-status-page">
      <Header />

      <main className="max-w-3xl mx-auto px-5 md:px-10 py-14 md:py-20">
        <div className="text-[11px] uppercase tracking-[0.32em] text-[#00f0ff] mb-5" data-testid="booking-status-ref">
          [ Booking · Ref {id.slice(0, 8).toUpperCase()} ]
        </div>
        <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.95]">
          Hi {(data.name || "there").split(" ")[0]},
          <br />
          {data.status === "approved" || data.status === "booked" ? (
            <span className="text-[#22c55e]">you&apos;re confirmed.</span>
          ) : data.status === "declined" ? (
            <span className="text-[#ff0055]">about your booking…</span>
          ) : data.status === "needs_info" ? (
            <span className="text-[#ffb800]">we need a quick detail.</span>
          ) : (
            <span className="italic font-light text-[#a395c3]">
              we&apos;re on it.
            </span>
          )}
        </h1>

        {/* Status timeline */}
        <div className="mt-12 rounded-3xl bg-[#0a0612]/80 border border-white/10 p-7 md:p-10" data-testid="booking-status-timeline">
          <div className="relative">
            <div className="absolute left-[15px] top-2 bottom-2 w-px bg-white/10" aria-hidden />
            {STEPS.map((s, i) => {
              const isDone = i < activeIdx || (isFinal && i === 2);
              const isCurrent = i === activeIdx;
              const isFinalStep = i === 2 && isFinal;
              const meta = isFinalStep ? final : null;
              const Icon = meta ? meta.icon : s.icon;
              const color = meta ? meta.color : (isDone ? "#22c55e" : isCurrent ? "#00f0ff" : "rgba(255,255,255,0.25)");
              const label = meta ? meta.label : s.label;
              return (
                <div
                  key={s.key}
                  data-testid={`booking-status-step-${s.key}`}
                  className="relative flex items-start gap-5 py-4"
                >
                  <div
                    className="relative w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 z-10"
                    style={{
                      background: isCurrent || isDone || isFinalStep ? `${color}22` : "rgba(255,255,255,0.03)",
                      border: `1.5px solid ${color}${isCurrent || isDone || isFinalStep ? "" : "40"}`,
                    }}
                  >
                    <Icon size={14} style={{ color }} />
                    {isCurrent && !isFinal && (
                      <span className="absolute inset-0 rounded-full animate-ping" style={{ background: `${color}30` }} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <div
                      className="font-display font-semibold text-base"
                      style={{ color: isCurrent || isDone || isFinalStep ? "#fff" : "rgba(255,255,255,0.4)" }}
                    >
                      {label}
                    </div>
                    {isCurrent && !isFinal && (
                      <div className="text-sm text-[#a395c3] mt-1">
                        {s.key === "pending_review" && "Your request is in the queue."}
                        {s.key === "vetting" && "Our booking concierge is reviewing the fit, date, and details."}
                      </div>
                    )}
                    {isFinalStep && meta && (
                      <div className="text-sm text-[#a395c3] mt-1.5 leading-relaxed">
                        {meta.copy}
                      </div>
                    )}
                    {isFinalStep && data.ai_verdict && (
                      <div
                        className="mt-3 rounded-xl p-3 border italic text-sm leading-relaxed"
                        style={{
                          background: `${final?.color || "#00f0ff"}10`,
                          borderColor: `${final?.color || "#00f0ff"}30`,
                          color: "rgba(255,255,255,0.85)",
                        }}
                        data-testid="booking-status-verdict"
                      >
                        {data.ai_verdict}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Booking details card */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="booking-status-details">
          <Field label="Package" value={data.package_label || data.package} accent="#00f0ff" />
          <Field
            label="Preferred date"
            value={
              data.preferred_date
                ? `${data.preferred_date}${data.preferred_time ? ` · ${data.preferred_time}` : ""}`
                : "—"
            }
            accent="#a395c3"
            icon={CalendarDays}
          />
          <Field
            label="Reference"
            value={id.slice(0, 8).toUpperCase()}
            accent="#a395c3"
            mono
          />
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-3 text-sm text-[#a395c3]">
          <span>Questions?</span>
          <a
            href="mailto:Info@sbmediastudio.com"
            className="inline-flex items-center gap-1.5 text-white hover:text-[#00f0ff] transition-colors"
            data-testid="booking-status-contact"
          >
            <Mail size={14} /> Info@sbmediastudio.com
          </a>
        </div>
      </main>
    </div>
  );
};

const Header = () => (
  <header className="border-b border-white/5 bg-[#05050a]/80 backdrop-blur-md sticky top-0 z-30">
    <div className="max-w-3xl mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
      <Link
        to="/"
        data-testid="booking-status-home"
        className="flex items-center gap-2 text-sm text-white/70 hover:text-white"
      >
        <ArrowLeft size={14} />
        <span>Back to SB Media Studio</span>
      </Link>
      <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3]">
        Booking status
      </div>
    </div>
  </header>
);

const Field = ({ label, value, accent, mono, icon: Icon }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    className="rounded-2xl bg-white/[0.03] border border-white/10 p-5"
  >
    <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] flex items-center gap-1.5">
      {Icon && <Icon size={11} />}
      {label}
    </div>
    <div
      className={`mt-2 font-display font-semibold text-base ${mono ? "font-mono tracking-wider" : ""}`}
      style={{ color: accent }}
    >
      {value}
    </div>
  </motion.div>
);

export default BookingStatusPage;
